import type { ReactNode } from 'react';

/**
 * 표 (19회차, 콘솔 검토 A-4(b)(d)(e)·D-2).
 *
 * 16회차까지 표 상자가 9곳, `thead` 클래스가 8곳, 행 구분선이 10곳에 복사돼 있었고
 * `<th>`에 `scope`가 하나도 없었다. 여기 하나로 모은다.
 *
 * 정렬은 **화면 안에서만** 한다(19회차 보완). 머리글에 `key`가 있는 열은 누르면 그 열로
 * 정렬하고, 이미 받아 둔 행만 다시 세운다. 서버 커서와 얽힌 정렬(콘솔 검토 C-9)은 여전히 밖이다.
 * 표 상자 둥글기·머리글 높이는 18회차까지의 값이다.
 *
 * 23-A회차(UI 피드백 18·20)에 **높이 상한이 기본값**이 됐다. 그전에는 표가 길어지면 화면이
 * 그만큼 늘어나 아래 구획이 저 밑으로 밀렸고, 스크롤을 내리면 머리글이 사라져 어느 열인지
 * 알 수 없었다. 이제 표는 24행쯤에서 자기 안에서만 스크롤하고 머리글은 붙어 있는다.
 * 페이징은 두지 않는다 — 표에 맞지 않고, 서버 상한에 걸린 표는 `LimitNote`가 한 줄로 알린다.
 */

/**
 * 기본 높이 상한. 행 하나가 36px(`px-4 py-2.5` + 12px 글자)이고 머리글이 37px이라
 * 24행 + 머리글이 이 값이다. 화면별로 더 짧게 두고 싶으면 `maxHeight`로 덮는다.
 */
export const DEFAULT_MAX_HEIGHT = 'max-h-[900px]';

export interface Column {
  label: string;
  /** 열 폭 유틸리티. 예 `w-44` */
  width?: string;
  align?: 'left' | 'right';
  /** 머리글에 붙는 한 줄 설명 */
  title?: string;
  /** 있으면 머리글을 눌러 이 키로 정렬한다 (`Table`의 `sort`와 함께 쓴다) */
  key?: string;
}

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key?: string;
  dir: SortDir;
  /** 머리글을 눌렀을 때. 같은 키를 다시 누르면 방향을 뒤집는 것은 부르는 쪽이 정한다 */
  onChange: (key: string) => void;
}

export function Table({
  columns,
  caption,
  minWidth,
  maxHeight,
  sort,
  children,
}: {
  columns: readonly Column[];
  /** 머리글 정렬. 없으면 머리글은 글자다 */
  sort?: SortState;
  /** 보조기술이 읽는 표 이름. 화면에는 안 보인다 */
  caption: string;
  /** 이 폭보다 좁아지면 가로로 민다. 예 `min-w-[900px]` */
  minWidth?: string;
  /**
   * 이 높이를 넘으면 표 안에서만 세로로 민다. 예 `max-h-[560px]`.
   * 기본은 24행 상당(`DEFAULT_MAX_HEIGHT`)이고, `'none'`을 주면 상한을 없앤다
   */
  maxHeight?: string;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-hair bg-panel">
      <div
        className={`overflow-auto ${maxHeight === 'none' ? '' : (maxHeight ?? DEFAULT_MAX_HEIGHT)}`}
      >
        <table className={`w-full text-left text-sm ${minWidth ?? ''}`}>
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-1 border-b border-line bg-band text-xs text-muted">
            <tr>
              {columns.map((column) => {
                const sortable = sort !== undefined && column.key !== undefined;
                const active = sortable && sort.key === column.key;
                return (
                  <th
                    key={column.label}
                    scope="col"
                    title={column.title}
                    aria-sort={
                      active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={`px-4 py-2.5 font-medium ${column.width ?? ''} ${
                      column.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => sort.onChange(column.key as string)}
                        className={`inline-flex items-center gap-1 whitespace-nowrap hover:text-ink ${
                          active ? 'text-ink' : ''
                        }`}
                      >
                        {column.label}
                        <span aria-hidden="true" className="text-[10px]">
                          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

/** 표 한 행. 누를 수 있는 행은 첫 칸에 링크가 있어야 키보드로 닿는다 (13회차) */
export function Row({
  children,
  onClick,
  tone,
}: {
  children: ReactNode;
  onClick?: () => void;
  /** 오류 행은 바탕을 옅게 물들인다 */
  tone?: 'error';
}) {
  const clickable = onClick !== undefined;
  return (
    <tr
      onClick={onClick}
      className={`border-b border-hair last:border-0 ${tone === 'error' ? 'bg-error/5' : ''} ${
        clickable ? 'cursor-pointer transition-colors duration-150 hover:bg-band' : ''
      }`}
    >
      {children}
    </tr>
  );
}

/** 표 한 칸. 자주 쓰는 조합만 이름을 붙였다 */
export function Cell({
  children,
  mono,
  nowrap,
  tone = 'ink',
  align,
  title,
  className,
  colSpan,
}: {
  children: ReactNode;
  mono?: boolean;
  nowrap?: boolean;
  tone?: 'ink' | 'muted' | 'error' | 'fail' | 'ok';
  align?: 'right';
  title?: string;
  /** 폭 제한·자르기처럼 열마다 다른 것 */
  className?: string;
  colSpan?: number;
}) {
  const TONE = {
    ink: 'text-ink',
    muted: 'text-muted',
    error: 'text-error',
    fail: 'text-fail',
    ok: 'text-ok',
  } as const;
  return (
    <td
      title={title}
      colSpan={colSpan}
      className={`px-4 py-2.5 text-xs ${TONE[tone]} ${mono ? 'font-mono' : ''} ${
        nowrap ? 'whitespace-nowrap' : ''
      } ${align === 'right' ? 'text-right' : ''} ${className ?? ''}`}
    >
      {children}
    </td>
  );
}
